import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { put, call } from 'redux-saga/effects';
import { addErrorToast } from '../../../../toast/slice';
import {
  resetTopologyLimits,
  setShowTopologyLimitWarningForEntity,
  setVmToAddFromTemplateTab,
} from '../../../../configuration/topologyLimits/slice';
import i18n from '../../../../../i18n';
import {
  willCpuLimitBeExceeded,
  willMemoryLimitBeExceeded,
} from '../../../../../pages/EditTopology/configuration/utils';
import { virtualMachineEntitiesApi } from '../../../../configuration/virtual-machine-entities/api';
import { setShowAddVmModal, setShowAddVmSavingModal } from '../../../slice';
import { refetchDiagramDataHelper } from '../../shared/sharedSagas';
import { prepMappingsForClone } from '../../../../../pages/EditTopology/configuration/VirtualMachines/Configure/Demos';
import { prepMappingsForCreate } from '../../../../../pages/EditTopology/configuration/components/VirtualMachineEntities/Configure/Templates/utils';

export function* addVirtualMachineFromTemplatesTabHelper(
  topologyUid: TopologyUid,
  vmNetworkInterfaces: VmToCloneVmNetworkInterface[] | NetworkToConfigure[],
  inventoryVm?: InventoryVirtualMachine,
): SagaIterator {
  if (!inventoryVm) {
    yield put(addErrorToast({ message: i18n.t('common.errorMessage') }));
    return;
  }

  let putResult;

  try {
    yield put(setShowAddVmSavingModal(true));
    if (inventoryVm.vm) {
      putResult = yield put(
        yield call(virtualMachineEntitiesApi.endpoints.cloneVm.initiate, {
          payload: {
            topology: topologyUid,
            vmNetworkInterfaces: prepMappingsForClone(vmNetworkInterfaces),
            vmUid: inventoryVm.vm.uid,
          },
        }),
      );
      yield putResult;
    } else {
      putResult = yield put(
        yield call(virtualMachineEntitiesApi.endpoints.createVm.initiate, {
          payload: {
            inventoryVmId: inventoryVm!.id,
            topology: topologyUid,
            vmNetworkInterfaces: prepMappingsForCreate(vmNetworkInterfaces),
          },
        }),
      );
      yield putResult;
    }
  } catch (error) {
    yield put(addErrorToast({ message: i18n.t('common.errorMessage') }));
  } finally {
    if (putResult) {
      yield call(putResult.unsubscribe);
    }
    yield call(refetchDiagramDataHelper, topologyUid.uid);
    yield put(setShowAddVmSavingModal(false));
    yield put(resetTopologyLimits());
  }
}

export function* handleAddVmFromTemplatesTabFromDiagramWorker({
  payload: { inventoryVm, topologyLimits, topologyUid, vmNetworkInterfaces },
}: PayloadAction<VirtualMachineToAddFromTemplatesTabFromDiagram>): SagaIterator {
  try {
    yield put(setShowAddVmModal(false));

    if (!inventoryVm) {
      yield put(addErrorToast({ message: i18n.t('common.errorMessage') }));
      return;
    }

    if (!topologyLimits) {
      yield call(
        addVirtualMachineFromTemplatesTabHelper,
        topologyUid,
        vmNetworkInterfaces,
        inventoryVm,
      );
      yield call(refetchDiagramDataHelper, topologyUid.uid);
      yield put(resetTopologyLimits());
      return;
    }

    const memoryLimitExceeded = yield call(
      willMemoryLimitBeExceeded,
      inventoryVm,
      topologyLimits.memoryMb,
    );

    const cpuLimitExceeded = yield call(
      willCpuLimitBeExceeded,
      inventoryVm,
      topologyLimits.cpuCores,
    );

    if (memoryLimitExceeded) {
      if (cpuLimitExceeded) {
        yield put(
          setShowTopologyLimitWarningForEntity(['Memory (GB)', 'vCPUs']),
        );
        yield put(
          setVmToAddFromTemplateTab({
            inventoryVm,
            topologyUid,
            vmNetworkInterfaces,
          }),
        );
      } else {
        yield put(setShowTopologyLimitWarningForEntity('Memory (GB)'));
        yield put(
          setVmToAddFromTemplateTab({
            inventoryVm,
            topologyUid,
            vmNetworkInterfaces,
          }),
        );
      }
    } else {
      if (cpuLimitExceeded) {
        yield put(setShowTopologyLimitWarningForEntity('vCPUs'));
        yield put(
          setVmToAddFromTemplateTab({
            inventoryVm,
            topologyUid,
            vmNetworkInterfaces,
          }),
        );
      } else {
        yield call(
          addVirtualMachineFromTemplatesTabHelper,
          topologyUid,
          vmNetworkInterfaces,
          inventoryVm,
        );
        yield call(refetchDiagramDataHelper, topologyUid.uid);
        yield put(resetTopologyLimits());
      }
    }
  } catch (error) {
    yield put(addErrorToast({ message: i18n.t('common.errorMessage') }));
  } finally {
    yield put(setShowAddVmModal(false));
  }
}
